/**
 * PressReleasesExtractionsApi
 *
 * Client-side wrapper for the admin press release extractions endpoint. Provides a
 * typed method to fetch all AI-extracted datapoints for a single issue
 * (`findImportantDatesByIssueId`) and returns structured TypeScript types
 * for important dates, offering terms/outcome, per-field provenance and
 * overall status. HTTP details are delegated to `ApiBase`; this module's
 * types document the expected server response shape for consumers.
 */
import ApiBase from './Api'

type ImportantDateValue = string | null

type ImportantDates = {
    generalMeetingDate: ImportantDateValue
    incRightsDate: ImportantDateValue
    exRightsDate: ImportantDateValue
    recordDate: ImportantDateValue
    rightsStartDate: ImportantDateValue
    rightsEndDate: ImportantDateValue
    subStartDate: ImportantDateValue
    subEndDate: ImportantDateValue
    ipoTradingDate: ImportantDateValue
}

type OfferingTermsValue = number | string | null

type OfferingTerms = {
    offeredUnits: OfferingTermsValue
    sharesInUnit: OfferingTermsValue
    unitSubPrice: OfferingTermsValue
    unitsReceived: OfferingTermsValue
    rightsReceived: OfferingTermsValue
    rightsRequired: OfferingTermsValue
    sharesRequired: OfferingTermsValue
    secondaryOffering: OfferingTermsValue
    overAllotmentSize: OfferingTermsValue
    minimumSubCondition: OfferingTermsValue
    offeredUnitsUnlisted: OfferingTermsValue
    overAllotmentSizeSecondary: OfferingTermsValue
}

type OfferingOutcome = {
    secondary_sub: OfferingTermsValue
    unit_sub_total: OfferingTermsValue
    unit_sub_guarantor: OfferingTermsValue
    unit_sub_with_rights: OfferingTermsValue
    unit_pct_sub_guarantor: OfferingTermsValue
    unit_sub_without_rights: OfferingTermsValue
    unit_pct_sub_with_rights: OfferingTermsValue
    over_allotment_allocation: OfferingTermsValue
    unit_pct_sub_without_rights: OfferingTermsValue
    over_allotment_allocation_secondary: OfferingTermsValue
}

type FieldProvenance = {
    sourceUrl: string | null
    sourcePages: number[]
    extractionId: string | null
    quote: string | null
}

// The fields we care about for now, status is based on how
// many of these are filled out.
export type ImportantDatesExtractionResponse = {
    issueId: string
    sourcesChecked: string[]
    importantDates: ImportantDates
    offeringTerms: OfferingTerms
    offeringOutcome?: OfferingOutcome
    offering_outcome?: OfferingOutcome
    provenanceByField: Record<string, FieldProvenance>
    status: 'ok' | 'partial' | 'empty'
}

const PRESS_RELEASES_EXTRACTIONS_API_PATHS = {
    // Issue-level endpoint so UI asks for exactly one issue at a time.
    ADMIN_PRESS_RELEASES_EXTRACTIONS: (issueId: string) => `/admin/pressreleasesextractions/${issueId}`,
}

class PressReleasesExtractionsApi extends ApiBase {
    async findImportantDatesByIssueId(issueId: string): Promise<ImportantDatesExtractionResponse | null> {
        if (!issueId) {
            return null
        }

        return await this._getRequest(PRESS_RELEASES_EXTRACTIONS_API_PATHS.ADMIN_PRESS_RELEASES_EXTRACTIONS(issueId))
    }
}

export default PressReleasesExtractionsApi
